import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, SectionTag } from "./shared";

const faqs = [
  {
    q: "Why didn't our operating partner find this during diligence?",
    a: "Operating diligence measures performance within functions — procurement savings, OEE, footprint, headcount. Trapped cash lives between functions, where each decision is locally defensible and invisible in the aggregate. It's a different framework, not a different team.",
  },
  {
    q: "How do we know this is actually recoverable — not just theoretical?",
    a: "Because our track record shows 68% realization: $185.9M identified, $127.7M actually recovered in 90-day timelines. We don't estimate what could be recovered; we deliver a roadmap your ops team can execute, month by month. Month 1 is quick wins (30–40%); Month 2 is process changes (50–60%); Month 3 is structural changes (80%+).",
  },
  {
    q: "What if management resists the plan?",
    a: "Management owns the validation. Every number is signed off by your CFO before you see it. It's their map, their plan, their commitments. That's why the recovery rate is 68% rather than 40% — when management owns the finding, they own the execution.",
  },
  {
    q: "How much disruption to the operation?",
    a: "None. This is a data-driven diagnostic. Five stakeholder interviews total, two days on-site. Your ops teams don't get pulled off; they get handed a 90-day roadmap and own the execution. We're out by day 7.",
  },
  {
    q: "Can this run inside a diligence timeline?",
    a: "Absolutely. Seven days. Before LOI, at close, or in-hold — the timeline and the fee don't change. For a diligence window, knowing what recoverable trapped cash is worth before you underwrite is exactly when you need this.",
  },
];

export const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-12 lg:py-16 bg-[#f7f9fa] dot-grid" data-testid="faq-section">
      <div className="max-w-[900px] mx-auto px-6 lg:px-10">
        <SectionTag num="08" label="Objections" />
        <Reveal>
          <h2 className="font-display font-extrabold text-[#0B1B2B] tracking-[-0.02em] leading-[1.05] text-3xl sm:text-4xl lg:text-[44px]">
            Here are the most <span className="text-[#3B7BC8]">frequently asked questions.</span>
          </h2>
        </Reveal>
        <div className="mt-9 space-y-3">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={i} delay={0.05 * i}>
                <div className={`rounded-2xl border transition-all duration-400 ${isOpen ? "bg-white border-[#0E5F46]/30 shadow-[0_14px_40px_rgba(11,27,43,0.08)]" : "bg-white/70 border-[#0B1B2B]/10 hover:border-[#0B1B2B]/25"}`}>
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${i + 1}`}
                    data-testid={`faq-question-${i + 1}`}
                    className="w-full flex items-center justify-between gap-6 text-left px-7 py-6"
                  >
                    <span className="font-display font-bold text-[#0B1B2B] text-[16px] lg:text-[17px] leading-snug">"{f.q}"</span>
                    <motion.span
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.3 }}
                      className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isOpen ? "bg-[#0E5F46] text-white" : "bg-[#0B1B2B]/5 text-[#0B1B2B]"}`}
                    >
                      <Plus className="w-4 h-4" strokeWidth={2.5} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p id={`faq-answer-${i + 1}`} className="px-7 pb-7 text-[#3c4a58] text-[15px] leading-relaxed max-w-[720px]" data-testid={`faq-answer-${i + 1}`}>{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
